import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { Visibility as ViewIcon } from "@mui/icons-material";

import { ChatSessionDto } from "@/apis/types/SuperChatLogTypes";

/**
 * 채팅 세션 타입 (확장)
 */
export interface ChatSession extends ChatSessionDto {
  primaryCategory?: string;
  sessionTitle?: string | null;
  sessionAdmFeedback?: string | null;
  hasFeedback?: boolean;
}

/**
 * 채팅 로그 리스트 Props
 */
export interface ChatLogListProps {
  sessions: ChatSession[];
  loading?: boolean;
  onViewSession: (sessionId: string) => void;
}

/**
 * 채팅 로그 리스트 컴포넌트
 *
 * @description 채팅 로그 목록을 표시하는 컴포넌트
 * @param props ChatLogListProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const List = (props: ChatLogListProps) => {
  const { sessions, loading = false, onViewSession } = props;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "ENDED":
        return "default";
      case "TIMEOUT":
        return "error";
      case "PAUSED":
        return "warning";
      default:
        return "default";
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "QNA":
        return "Q&A 문의";
      case "EVENT_APPLY":
        return "이벤트 신청";
      case "DEMO_REQUEST":
        return "데모 요청";
      case "CONSULTATION":
        return "상담 문의";
      case "SUPPORT":
        return "기술 지원";
      case "GENERAL":
        return "일반문의";
      case "TECHNICAL":
        return "기술지원";
      case "BILLING":
        return "요금문의";
      case "SERVICE":
        return "서비스소개";
      default:
        return "기타";
    }
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>세션 ID</TableCell>
              <TableCell>사용자</TableCell>
              <TableCell>카테고리</TableCell>
              <TableCell align="center">메시지 수</TableCell>
              <TableCell align="center">상태</TableCell>
              <TableCell align="center">피드백</TableCell>
              <TableCell>시작 시간</TableCell>
              <TableCell>종료 시간</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  채팅 로그가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => (
                <TableRow key={session.sessionId} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {session.sessionId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {session.user?.userName ||
                          session.sessionUserId ||
                          "게스트"}
                      </Typography>
                      {session.user?.userEmail && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          📧 {session.user.userEmail}
                        </Typography>
                      )}
                      {session.user?.userPhone && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          📞 {session.user.userPhone}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryLabel(
                        session.primaryCategory || session.sessionType
                      )}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {session.totalMessages || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={session.sessionStatus}
                      size="small"
                      color={getStatusColor(session.sessionStatus)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {session.hasFeedback ? (
                      <Chip
                        label="있음"
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        label="없음"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(session.startedAt).toLocaleString("ko-KR")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.endedAt
                        ? new Date(session.endedAt).toLocaleString("ko-KR")
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onViewSession(session.sessionId)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
